/**
 * DocumentsTab — the study's documents.
 *
 * Registry mode (source-of-truth plan §5.1 — linked study + flip on): the tab
 * IS the shared StudyDocumentsCard reading eReg's current versions through the
 * CRM proxy; uploads are eReg deep links with the document role prefilled.
 * Any not-yet-migrated legacy uploads stay listed read-only below.
 *
 * Legacy mode (unlinked study, or the §7 rollback flag off): the original
 * upload-to-CRM flow, unchanged — files go to the KMS-encrypted CRM bucket;
 * extraction runs in the crm-worker and the chip flips pending -> processing
 * -> ready (StudyDetail polls while busy). Unlinked studies see a link-first
 * prompt above it (§6-E1).
 */
import { Link, useLocation } from 'react-router-dom';
import { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, ExternalLink, FileText, Loader2, RotateCcw, Upload } from 'lucide-react';
import api from '../../../services/api';
import { StudyDetailResponse, StudyDocument } from '../../types';
import { relTime } from '../../lib';
import StudyDocumentsCard from '../../../ui/StudyDocumentsCard';
import { eregStudyUploadLink } from '../../../ui/returnTo';

const DOC_TYPES = [
  { value: 'protocol', label: 'Protocol' },
  { value: 'icf', label: 'Informed consent (ICF)' },
  { value: 'amendment', label: 'Amendment' },
  { value: 'other', label: 'Other' },
];

function statusChip(doc: StudyDocument) {
  switch (doc.extraction_status) {
    case 'complete':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
          <CheckCircle2 className="h-3 w-3" /> Ready
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
          <AlertCircle className="h-3 w-3" /> Failed
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
          <Loader2 className="h-3 w-3 animate-spin" /> {doc.extraction_status === 'processing' ? 'Processing' : 'Queued'}
        </span>
      );
  }
}

const fmtSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
};

export default function DocumentsTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const location = useLocation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [docType, setDocType] = useState('protocol');
  const [versionLabel, setVersionLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const registryEnabled = detail.registry_reads_enabled !== false;
  const linkedProtocolId = detail.study.protocol_id || null;
  // Gate on the RESOLVED registry record, not just the link column — a
  // degraded/foreign link must fall back to the legacy view instead of a
  // dead-ended card + upload links.
  const registryMode = registryEnabled && !!linkedProtocolId && detail.registry != null;
  const returnTo = `${location.pathname}?tab=documents`;

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Pick a PDF first');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('doc_type', docType);
      if (versionLabel.trim()) form.append('version_label', versionLabel.trim());
      await api.post(`/api/crm/studies/${detail.study.id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (fileRef.current) fileRef.current.value = '';
      setVersionLabel('');
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const view = async (doc: StudyDocument) => {
    try {
      const res = await api.get(`/api/crm/study-documents/${doc.id}/url`);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err: any) {
      window.alert(err?.response?.data?.error || 'Could not open document');
    }
  };

  const [retrying, setRetrying] = useState<string | null>(null);
  const retryExtraction = async (doc: StudyDocument) => {
    setRetrying(doc.id);
    try {
      await api.post(`/api/crm/study-documents/${doc.id}/extract`, {});
      onChanged();
    } catch (err: any) {
      window.alert(err?.response?.data?.error || 'Could not retry extraction');
    } finally {
      setRetrying(null);
    }
  };

  // ---- Registry mode: eReg is the document source (plan §5.1) --------------
  if (registryMode) {
    return (
      <div className="space-y-4">
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={eregStudyUploadLink(linkedProtocolId!, { role: 'protocol', returnTo, returnLabel: 'CRM Study' })}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <Upload className="h-4 w-4" /> Upload protocol in eReg
            </Link>
            <Link
              to={eregStudyUploadLink(linkedProtocolId!, { role: 'icf', returnTo, returnLabel: 'CRM Study' })}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              <Upload className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Upload ICF in eReg
            </Link>
          </div>
        )}

        <StudyDocumentsCard
          protocolId={linkedProtocolId!}
          apiBase="/api/crm/registry"
          canManageInEreg={isAdmin}
          returnTo={returnTo}
          returnLabel="CRM Study"
        />

        {detail.documents.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Legacy uploads</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Files uploaded before this study read from eReg. Migrate them via CRM Settings → Registry backfill; new versions go to eReg.
            </p>
            <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
              {detail.documents.map((doc) => (
                <li key={doc.id} className="flex flex-wrap items-center gap-3 py-3 sm:flex-nowrap">
                  <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{doc.filename}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type}
                      {doc.version_label && <> · {doc.version_label}</>}
                      {doc.created_at && <> · {relTime(doc.created_at)} ago</>}
                    </p>
                  </div>
                  {statusChip(doc)}
                  <button
                    type="button"
                    onClick={() => view(doc)}
                    aria-label={`Open ${doc.filename}`}
                    className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // ---- Legacy mode (unlinked study or flip off) -----------------------------
  // The link-first prompt for unlinked studies is the GLOBAL StudyDetail
  // banner (always visible above the tabs) — no duplicate banner here.
  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Upload a document</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[14rem] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">PDF file (max 50MB)</label>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf"
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100 dark:text-gray-300 dark:file:bg-primary-900/30 dark:file:text-primary-300"
              />
            </div>
            <div className="w-48">
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="w-48">
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Version label (optional)</label>
              <input
                type="text"
                placeholder="e.g. Protocol v3.0"
                value={versionLabel}
                onChange={(e) => setVersionLabel(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <button
              type="button"
              onClick={upload}
              disabled={uploading}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Documents are stored encrypted. Text is extracted automatically so the AI can generate the summary,
            script, and prescreen — usually a minute or two.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Documents</h2>
        {detail.documents.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Nothing uploaded yet.{isAdmin && ' Start with the study protocol — it powers every AI asset.'}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {detail.documents.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center gap-3 py-3 sm:flex-nowrap">
                <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{doc.filename}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {DOC_TYPES.find((t) => t.value === doc.doc_type)?.label || doc.doc_type}
                    {doc.version_label && <> · {doc.version_label}</>}
                    {doc.page_count != null && <> · {doc.page_count} pages</>}
                    {doc.file_size != null && <> · {fmtSize(doc.file_size)}</>}
                    <> · {relTime(doc.created_at)} ago</>
                  </p>
                  {doc.extraction_status === 'failed' && doc.extraction_error && (
                    <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{doc.extraction_error}</p>
                  )}
                </div>
                {statusChip(doc)}
                {isAdmin && doc.extraction_status === 'failed' && (
                  <button
                    type="button"
                    onClick={() => retryExtraction(doc)}
                    disabled={retrying === doc.id}
                    className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    {retrying === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => view(doc)}
                  aria-label={`Open ${doc.filename}`}
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
